import {
  COLORS,
  GameEngine,
  HINT_REVEALS_PER_LEVEL,
  computeHint,
  type HintKind,
  type Level,
} from '@color-dots/game';
import { syncScore } from './auth.js';
import {
  generateDailyLevel,
  isDailyCompletedToday,
  loadDailyProgress,
  markDailyCompleted,
  type DailyProgress,
} from './daily.js';
import {
  generateEndlessLevel,
  loadEndlessProgress,
  recordEndlessRun,
} from './endless.js';
import { CanvasRenderer } from './game/CanvasRenderer.js';
import { InputController } from './game/InputController.js';
import { preloadGearRimSprites } from './game/gearRimSprites.js';
import { GearSoundPlayer } from './game/gearSound.js';
import { BackgroundMusicPlayer } from './game/backgroundMusic.js';
import {
  collectGearSoundEvents,
  createGearSoundTracker,
  resetGearSoundTracker,
} from './game/gearSoundEvents.js';
import { getAudioTheme } from './game/audioThemes.js';
import {
  getVisualStyleForLevel,
  getVisualStyleLabel,
  type VisualStyle,
} from './game/visualStyles.js';
import { InputRouter } from './input/InputRouter.js';
import { getLevelById, loadAllLevels, type LoadedLevels } from './levels/loader.js';
import { Scene3DApp } from './scene3d/Scene3DApp.js';
import type { ViewportInsets } from './scene3d/CameraRig.js';
import {
  calculateStars,
  getCompletedLevelIds,
  getLastLevelId,
  getParTimeSec,
  markLevelCompleted,
  setLastLevel,
} from './progress.js';
import {
  applyAnimationSetting,
  canUse3D,
  clampLevelToTier,
  difficultyTierLabel,
  filterLevelsByTier,
  getStoredSettings,
  levelDifficultyLabel,
  resolveGameMode,
  saveSettings,
  type GameMode,
  type GameSettings,
} from './settings.js';
import {
  cycleTheme,
  getStoredTheme,
  listenSystemThemeChange,
  themeLabel,
} from './theme.js';
import { openLevelPicker } from './ui/levelPicker.js';

const SETTINGS_GEAR_SVG = `
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
`;

/** Russian plural for "день" (1 день, 2-4 дня, 5+ дней, with the usual 11-14 exception). */
function daysWord(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return 'дней';
  const mod10 = n % 10;
  if (mod10 === 1) return 'день';
  if (mod10 >= 2 && mod10 <= 4) return 'дня';
  return 'дней';
}

function renderWinStars(stars: number): string {
  const spans = [1, 2, 3]
    .map((n) => `<span class="win-star${n <= stars ? ' win-star--filled' : ''}">★</span>`)
    .join('');
  return `<div class="win-stars" role="img" aria-label="${stars} из 3 звёзд">${spans}</div>`;
}

function formatMinSec(totalSec: number): string {
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function renderWinTimeInfo(timeSec: number, parTimeSec: number, missedTimeBonusOnly: boolean): string {
  const time = formatMinSec(timeSec);
  if (missedTimeBonusOnly) {
    return `<p class="win-time">⏱ ${time} — уложись в ${formatMinSec(parTimeSec)}, чтобы получить 3★</p>`;
  }
  return `<p class="win-time">⏱ ${time}</p>`;
}

export class App {
  private root: HTMLElement;
  private engine: GameEngine | null = null;
  private currentLevel: Level | null = null;
  private activeHintKind: HintKind | null = null;
  private canvasRenderer: CanvasRenderer | null = null;
  private scene3d: Scene3DApp | null = null;
  private input2d: InputController | null = null;
  private input3d: InputRouter | null = null;
  private pack: LoadedLevels | null = null;
  private currentLevelId = getLastLevelId();
  private completedLevels = getCompletedLevelIds();
  /** Non-null while playing a procedurally generated level (daily/endless) that
   *  isn't part of `this.pack` — routes handleWin() to its own bookkeeping/modal
   *  instead of the normal pack-based completedLevels/stars/next-level flow. */
  private specialMode: 'daily' | 'endless' | null = null;
  private endlessRound = 0;
  private settings: GameSettings = getStoredSettings();
  private mode: GameMode;
  private gearSoundPlayer = new GearSoundPlayer(getStoredSettings().sound);
  private musicPlayer = new BackgroundMusicPlayer(getStoredSettings().music);
  private gearSoundTracker = createGearSoundTracker();
  private currentVisualStyle: VisualStyle = 'pipe';
  /** Wall-clock start of the current attempt (Date.now()), reset on level load/replay —
   *  feeds the 3rd-star "finish within par time" check in calculateStars(). */
  private levelStartedAt = 0;
  private timerIntervalId: number | null = null;
  /** Set while the tab/screen is hidden (backgrounded or locked) — on resume, the
   *  paused span is added back onto `levelStartedAt` so it never counts toward the
   *  timer or the 3rd-star par-time check. Also frozen while settings / the level
   *  picker are open and while the player pauses manually. */
  private pausedAt: number | null = null;
  /** Why the clock is currently frozen — it runs only while this is empty. */
  private pauseReasons = new Set<'hidden' | 'settings' | 'picker' | 'manual'>();
  private pauseBtn: HTMLButtonElement | null = null;
  private pauseOverlay: HTMLElement | null = null;

  private canvas!: HTMLCanvasElement;
  private undoBtn!: HTMLButtonElement;
  private hintNudgeBtn: HTMLButtonElement | null = null;
  private hintRevealBtn: HTMLButtonElement | null = null;
  private hintRevealBadge: HTMLElement | null = null;
  private hintStatus: HTMLElement | null = null;
  private levelTimerEl: HTMLElement | null = null;
  private clearBtn!: HTMLButtonElement;
  private resetBtn!: HTMLButtonElement;
  private levelLabel!: HTMLElement;
  private prevLevelBtn!: HTMLButtonElement;
  private nextLevelBtn!: HTMLButtonElement;
  private levelNav: HTMLElement | null = null;
  private winOverlay: HTMLElement | null = null;
  private settingsOverlay: HTMLElement | null = null;
  private levelPickerOverlay: HTMLElement | null = null;
  private themeBtn!: HTMLButtonElement;
  private settingsBtn!: HTMLButtonElement;
  private viewBtn: HTMLButtonElement | null = null;
  private rotateLeftBtn!: HTMLButtonElement;
  private rotateRightBtn!: HTMLButtonElement;

  constructor(root: HTMLElement) {
    this.root = root;
    this.mode = resolveGameMode(this.settings);
    applyAnimationSetting(this.settings.animations);
    this.renderShell();
    this.setupTheme();
    document.addEventListener('visibilitychange', () => this.handleVisibilityChange());
    void this.init();
  }

  private renderShell(): void {
    const is3d = this.mode === '3d';

    this.root.innerHTML = `
      <div class="app-shell ${is3d ? 'app-shell--3d' : ''}">
        ${is3d ? `
        <div class="scene-container" id="scene-container">
          <canvas id="game-board" aria-label="Игровое поле 3D"></canvas>
          <div class="level-hud">
            <div class="level-timer" id="level-timer" aria-hidden="true">0:00</div>
            <button class="level-pause-btn" id="pause-btn" type="button" title="Пауза" aria-label="Пауза">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
            </button>
          </div>
          <header class="game-overlay game-overlay--top">
          <button class="overlay-btn" id="menu-btn" type="button" title="Уровни" aria-label="Выбор уровня">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M4 6h16M4 12h16M4 18h16"/>
            </svg>
          </button>
          <button class="overlay-btn" id="prev-level-btn" type="button" title="Предыдущий уровень" aria-label="Предыдущий уровень">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M15 6l-6 6 6 6"/>
            </svg>
          </button>
          <div class="overlay-level level-label--btn" id="level-label" role="button" tabindex="0">Загрузка…</div>
          <button class="overlay-btn" id="next-level-nav-btn" type="button" title="Следующий уровень" aria-label="Следующий уровень">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M9 6l6 6-6 6"/>
            </svg>
          </button>
          <button class="overlay-btn" id="view-btn" type="button" title="Сменить обзор" aria-label="Сменить обзор камеры">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3"/>
              <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z"/>
            </svg>
          </button>
          <button class="overlay-btn" id="settings-btn" type="button" title="Настройки" aria-label="Настройки">
            ${SETTINGS_GEAR_SVG}
          </button>
          <button class="overlay-btn" id="theme-btn" type="button" title="Тема">
            <svg class="theme-icon theme-icon--light" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="4"/>
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
            </svg>
            <svg class="theme-icon theme-icon--dark" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
            <svg class="theme-icon theme-icon--system" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="2" y="3" width="20" height="14" rx="2"/>
              <path d="M8 21h8M12 17v4"/>
            </svg>
          </button>
        </header>
        <nav class="level-nav level-nav--overlay" id="level-nav" aria-label="Уровни" hidden></nav>
        <footer class="game-overlay game-overlay--bottom">
          <button class="overlay-action" id="undo-btn" type="button" disabled aria-label="Отменить">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M9 14 4 9l5-5"/>
              <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11"/>
            </svg>
            <span>Отменить</span>
          </button>
          <button class="overlay-action" id="clear-btn" type="button" aria-label="Очистить">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M3 6h18"/>
              <path d="M8 6V4h8v2"/>
              <path d="M19 6l-1 14H6L5 6"/>
            </svg>
            <span>Очистить</span>
          </button>
          <button class="overlay-action" id="reset-btn" type="button" aria-label="Сброс уровня">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
              <path d="M3 3v5h5"/>
            </svg>
            <span>Сброс</span>
          </button>
        </footer>
          <div class="scene-hint" aria-live="polite">
            Потяни влево/вправо: поворот • вверх/вниз: наклон
          </div>
          <div class="rotate-controls" aria-label="Управление вращением">
            <button class="rotate-btn" id="rotate-left" type="button" aria-label="Вращать против часовой стрелки">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M3 12a9 9 0 1 0 9-9"/>
                <path d="M3 3v5h5"/>
              </svg>
            </button>
            <button class="rotate-btn" id="rotate-right" type="button" aria-label="Вращать по часовой стрелке">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 12a9 9 0 1 1-9-9"/>
                <path d="M16 3h5v5"/>
              </svg>
            </button>
          </div>
        </div>
        ` : `
        <header class="top-bar">
          <button class="icon-btn" id="menu-btn" type="button" title="Уровни" aria-label="Выбор уровня">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M4 6h16M4 12h16M4 18h16"/>
            </svg>
          </button>
          <div class="brand">
            <div class="brand-icon" aria-hidden="true">
              <img src="/logo.svg" width="44" height="44" alt="" />
            </div>
            <div class="brand-text">
              <h1>Color Dots</h1>
              <div class="level-nav-row">
                <button class="icon-btn icon-btn--small" id="prev-level-btn" type="button" title="Предыдущий уровень" aria-label="Предыдущий уровень">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M15 6l-6 6 6 6"/>
                  </svg>
                </button>
                <button class="level-label level-label--btn" id="level-label" type="button">Загрузка…</button>
                <button class="icon-btn icon-btn--small" id="next-level-nav-btn" type="button" title="Следующий уровень" aria-label="Следующий уровень">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M9 6l6 6-6 6"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div class="top-bar-actions">
            <button class="icon-btn" id="settings-btn" type="button" title="Настройки" aria-label="Настройки">
              ${SETTINGS_GEAR_SVG}
            </button>
            <button class="icon-btn" id="theme-btn" type="button" title="Тема">
              <svg class="theme-icon theme-icon--light" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="4"/>
                <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
              </svg>
              <svg class="theme-icon theme-icon--dark" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
              <svg class="theme-icon theme-icon--system" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="2" y="3" width="20" height="14" rx="2"/>
                <path d="M8 21h8M12 17v4"/>
              </svg>
            </button>
            <button class="icon-btn" id="reset-btn" type="button" title="Сброс">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                <path d="M3 3v5h5"/>
              </svg>
            </button>
          </div>
        </header>
        <div class="board-wrap">
          <canvas id="game-board" aria-label="Игровое поле"></canvas>
          <div class="level-hud">
            <div class="level-timer" id="level-timer" aria-hidden="true">0:00</div>
            <button class="level-pause-btn" id="pause-btn" type="button" title="Пауза" aria-label="Пауза">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
            </button>
          </div>
        </div>
        <div class="hint-status" id="hint-status" aria-live="polite" hidden></div>
        <footer class="action-bar">
          <button class="action-btn" id="undo-btn" type="button" disabled>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M9 14 4 9l5-5"/>
              <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11"/>
            </svg>
            Отменить
          </button>
          <button class="action-btn" id="hint-nudge-btn" type="button" title="Направление">
            🎯 Направление
          </button>
          <button class="action-btn" id="hint-reveal-btn" type="button" title="Путь">
            💡 Путь<span class="hint-reveal-badge" id="hint-reveal-badge"></span>
          </button>
        </footer>
        `}
      </div>
    `;

    this.canvas = this.root.querySelector('#game-board') as HTMLCanvasElement;
    this.canvas.addEventListener('pointerdown', () => {
      this.gearSoundPlayer.resume();
      this.musicPlayer.resume();
    });
    this.undoBtn = this.root.querySelector('#undo-btn') as HTMLButtonElement;
    this.hintNudgeBtn = this.root.querySelector('#hint-nudge-btn');
    this.hintRevealBtn = this.root.querySelector('#hint-reveal-btn');
    this.hintRevealBadge = this.root.querySelector('#hint-reveal-badge');
    this.hintStatus = this.root.querySelector('#hint-status');
    this.levelTimerEl = this.root.querySelector('#level-timer');
    this.pauseBtn = this.root.querySelector('#pause-btn');
    this.pauseOverlay = null;
    this.pauseReasons.delete('manual');
    if (this.pausedAt !== null && this.pauseReasons.size === 0) {
      this.levelStartedAt += Date.now() - this.pausedAt;
      this.pausedAt = null;
    }
    this.resetBtn = this.root.querySelector('#reset-btn') as HTMLButtonElement;
    this.levelLabel = this.root.querySelector('#level-label') as HTMLElement;
    this.levelNav = this.root.querySelector('#level-nav');
    this.themeBtn = this.root.querySelector('#theme-btn') as HTMLButtonElement;
    this.settingsBtn = this.root.querySelector('#settings-btn') as HTMLButtonElement;
    this.prevLevelBtn = this.root.querySelector('#prev-level-btn') as HTMLButtonElement;
    this.nextLevelBtn = this.root.querySelector('#next-level-nav-btn') as HTMLButtonElement;

    const menuBtn = this.root.querySelector('#menu-btn') as HTMLButtonElement | null;
    menuBtn?.addEventListener('click', () => this.showLevelPicker());

    this.prevLevelBtn.addEventListener('click', () => {
      const prevLevelId = this.getPrevLevelId();
      if (prevLevelId) this.loadLevel(prevLevelId);
    });
    this.nextLevelBtn.addEventListener('click', () => {
      const nextLevelId = this.getNextLevelId();
      if (nextLevelId) this.loadLevel(nextLevelId);
    });

    this.levelLabel.addEventListener('click', () => this.showLevelPicker());
    this.levelLabel.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.showLevelPicker();
      }
    });

    if (is3d) {
      this.rotateLeftBtn = this.root.querySelector('#rotate-left') as HTMLButtonElement;
      this.rotateRightBtn = this.root.querySelector('#rotate-right') as HTMLButtonElement;
      this.clearBtn = this.root.querySelector('#clear-btn') as HTMLButtonElement;
      this.viewBtn = this.root.querySelector('#view-btn') as HTMLButtonElement;

      this.clearBtn.addEventListener('click', () => this.handleClear());
      this.viewBtn.addEventListener('click', () => this.handleViewChange());
    }

    this.updateThemeButton();
    this.themeBtn.addEventListener('click', () => {
      cycleTheme();
      this.updateThemeButton();
      this.redrawBoard();
    });

    this.settingsBtn.addEventListener('click', () => this.showSettingsModal());
    this.pauseBtn?.addEventListener('click', () => this.togglePause());

    this.undoBtn.addEventListener('click', () => this.handleUndo());
    this.hintNudgeBtn?.addEventListener('click', () => this.handleHint('nudge'));
    this.hintRevealBtn?.addEventListener('click', () => this.handleHint('reveal'));
    this.resetBtn.addEventListener('click', () => this.handleReset());
    window.addEventListener('resize', () => this.handleResize());
  }

  /** Pauses the level timer's wall-clock basis while the tab is hidden (backgrounded
   *  or the screen is locked), so a player who gets interrupted mid-level doesn't
   *  lose the 3rd-star time bonus to time they weren't actually playing. */
  private handleVisibilityChange(): void {
    this.setPause('hidden', document.hidden);
  }

  /** Freezes / resumes the level clock. The clock runs only while no reason is active. */
  private setPause(reason: 'hidden' | 'settings' | 'picker' | 'manual', on: boolean): void {
    const wasPaused = this.pauseReasons.size > 0;
    if (on) this.pauseReasons.add(reason);
    else this.pauseReasons.delete(reason);
    const isPaused = this.pauseReasons.size > 0;

    if (!wasPaused && isPaused) {
      this.pausedAt = Date.now();
    } else if (wasPaused && !isPaused && this.pausedAt !== null) {
      this.levelStartedAt += Date.now() - this.pausedAt;
      this.pausedAt = null;
    }
    this.updateLevelTimerDisplay();
    if (reason === 'manual') this.syncPauseUi();
  }

  private nowForTimer(): number {
    return this.pausedAt ?? Date.now();
  }

  private togglePause(): void {
    this.setPause('manual', !this.pauseReasons.has('manual'));
  }

  /** Cover the board while manually paused (so the player can't study it with the
   *  clock stopped) and flip the pause button's icon between pause and play. */
  private syncPauseUi(): void {
    const paused = this.pauseReasons.has('manual');
    if (this.pauseBtn) {
      this.pauseBtn.title = paused ? 'Продолжить' : 'Пауза';
      this.pauseBtn.setAttribute('aria-label', this.pauseBtn.title);
      this.pauseBtn.innerHTML = paused
        ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>'
        : '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>';
    }
    if (!paused) {
      this.pauseOverlay?.remove();
      this.pauseOverlay = null;
      return;
    }
    const host = this.canvas.parentElement;
    if (!host || this.pauseOverlay) return;
    const overlay = document.createElement('div');
    overlay.className = 'pause-overlay';
    overlay.innerHTML = `
      <button class="pause-overlay-btn" type="button" aria-label="Продолжить">
        <svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
      </button>
      <div class="pause-overlay-text">Пауза</div>`;
    overlay.addEventListener('click', () => this.setPause('manual', false));
    host.appendChild(overlay);
    this.pauseOverlay = overlay;
  }

  private setupTheme(): void {
    this.updateThemeButton();
    listenSystemThemeChange(() => {
      this.updateThemeButton();
      this.redrawBoard();
    });
    document.addEventListener('themechange', () => {
      this.updateThemeButton();
      this.redrawBoard();
    });
  }

  private updateThemeButton(): void {
    const mode = getStoredTheme();
    this.themeBtn.title = themeLabel(mode);
    this.themeBtn.dataset.themeMode = mode;
  }

  private redrawBoard(): void {
    if (!this.engine) return;
    if (this.mode === '3d' && this.scene3d) {
      this.scene3d.applyTheme();
      this.scene3d.sync(
        this.engine.getState(),
        this.input3d?.getActiveColor() ?? null,
      );
    } else if (this.canvasRenderer) {
      this.canvasRenderer.render(
        this.engine.getState(),
        this.input2d?.getActiveColor() ?? null,
      );
    }
  }

  private async init(): Promise<void> {
    try {
      this.pack = await loadAllLevels();
      const startLevel = clampLevelToTier(
        this.currentLevelId,
        this.settings.difficultyTier,
        this.pack.levels,
      );
      this.currentLevelId = startLevel;
      this.loadLevel(startLevel);
    } catch (error) {
      this.setLevelLabelText('Ошибка загрузки уровней');
      console.error(error);
    }
  }

  private getVisibleLevels() {
    if (!this.pack) return [];
    return filterLevelsByTier(this.pack.levels, this.settings.difficultyTier);
  }


  private destroyInput(): void {
    this.input2d?.destroy();
    this.input2d = null;
    this.input3d?.destroy();
    this.input3d = null;
  }

  private destroyRenderer(): void {
    this.scene3d?.destroy();
    this.scene3d = null;
    this.canvasRenderer = null;
  }

  /** Walled levels only render in 2D (see doc/03-game-mechanics.md §3.11) — the 3D
   *  board doesn't know how to draw a blocked cell, so force 2D for those regardless
   *  of the player's 3D preference, and restore that preference once they move on. */
  private effectiveModeFor(level: Level): GameMode {
    if ((level.walls?.length ?? 0) > 0) return '2d';
    return resolveGameMode(this.settings);
  }

  private ensureModeMatches(level: Level): void {
    const desired = this.effectiveModeFor(level);
    if (desired === this.mode) return;

    this.dismissWinModal();
    this.destroyInput();
    this.destroyRenderer();
    this.engine = null;
    this.mode = desired;
    this.renderShell();
    this.setupTheme();
  }

  private loadLevel(levelId: string): void {
    if (!this.pack) return;
    const level = getLevelById(this.pack.levels, levelId);
    if (!level) return;

    this.specialMode = null;
    setLastLevel(levelId);

    const styleLabel = getVisualStyleLabel(getVisualStyleForLevel(levelId));
    const difficultyLabel = levelDifficultyLabel(level.difficulty);
    const levelText = `Уровень ${levelId.replace('e-', '')} · ${level.size}×${level.size} · ${styleLabel} · ${difficultyLabel}`;

    this.loadGeneratedLevel(level, levelText);
    this.updateLevelNavButtons();
  }

  /** Starts (or resumes, via `round`) the "how far can you get" mode: procedurally
   *  generated levels of gradually increasing difficulty, no fixed end. */
  private startEndlessLevel(round: number): void {
    this.dismissLevelPicker();
    this.specialMode = 'endless';
    this.endlessRound = round;
    const level = generateEndlessLevel(round);
    this.loadGeneratedLevel(level, `Бесконечный режим · раунд ${round} · ${level.size}×${level.size}`);
  }

  /** Starts today's shared puzzle — same seed for every player on this calendar date. */
  private startDailyLevel(): void {
    this.dismissLevelPicker();
    this.specialMode = 'daily';
    const level = generateDailyLevel();
    this.loadGeneratedLevel(level, `Уровень дня · ${level.size}×${level.size}`);
  }

  /** Leaves daily/endless mode and resumes wherever the player was in the normal
   *  pack — `progress.ts`'s `lastLevel` was never overwritten while in a special
   *  mode (only `loadLevel` calls `setLastLevel`), so it's still pointing there. */
  private exitSpecialMode(): void {
    this.loadLevel(getLastLevelId());
  }

  /** Shared build step for any `Level` object, whether it came from `this.pack`
   *  or was generated on the fly (daily/endless) — engine, renderer, input,
   *  label, resize. Callers own the label text and any pack-specific bookkeeping
   *  (`setLastLevel`, nav buttons) that doesn't apply to generated levels. */
  private loadGeneratedLevel(level: Level, labelText: string): void {
    this.ensureModeMatches(level);

    this.currentLevelId = level.id;
    this.currentLevel = level;
    this.activeHintKind = null;
    this.dismissWinModal();

    this.destroyInput();
    this.destroyRenderer();

    const visualStyle = getVisualStyleForLevel(level.id);
    this.currentVisualStyle = visualStyle;
    this.musicPlayer.setTheme(getAudioTheme(visualStyle));
    // gear keeps its own clinks/ratchet; every other style plays its themed SFX
    this.gearSoundPlayer.setTheme(visualStyle === 'gear' ? null : getAudioTheme(visualStyle));
    resetGearSoundTracker(this.gearSoundTracker);
    if (visualStyle === 'gear') {
      void preloadGearRimSprites();
    }

    this.engine = new GameEngine(level);

    if (this.mode === '3d') {
      this.scene3d = new Scene3DApp(this.canvas, { visualStyle });
      this.scene3d.build(this.engine.getState());
      this.input3d = new InputRouter(
        this.canvas,
        this.scene3d,
        this.engine,
        () => this.syncUi(),
        () => this.handleWin(),
        this.rotateLeftBtn,
        this.rotateRightBtn,
      );
    } else {
      this.canvasRenderer = new CanvasRenderer(this.canvas, { visualStyle });
      this.canvasRenderer.setRenderCallback(() => this.syncUi());
      this.canvasRenderer.setGearSoundCallback((event) => this.gearSoundPlayer.play(event));
      this.input2d = new InputController(
        this.canvas,
        this.canvasRenderer,
        this.engine,
        () => {
          this.clearHint();
          this.syncUi();
        },
        () => this.handleWin(),
      );
    }

    this.setLevelLabelText(labelText);
    this.handleResize();
    this.startLevelTimer();
    this.syncUi();
  }

  private handleResize(): void {
    if (!this.engine) return;

    if (this.mode === '3d' && this.scene3d) {
      this.scene3d.resize(this.getViewportInsets());
      this.scene3d.sync(
        this.engine.getState(),
        this.input3d?.getActiveColor() ?? null,
      );
      return;
    }

    if (!this.canvasRenderer) return;
    const wrap = this.canvas.parentElement;
    if (!wrap) return;

    const shell = wrap.closest('.app-shell') as HTMLElement | null;
    const topBar = this.root.querySelector('.top-bar') as HTMLElement | null;
    const actionBar = this.root.querySelector('.action-bar') as HTMLElement | null;

    const topBarH = topBar?.getBoundingClientRect().height ?? 0;
    const topBarMarginBottom = topBar ? parseFloat(getComputedStyle(topBar).marginBottom) || 0 : 0;
    const actionBarH = actionBar?.getBoundingClientRect().height ?? 0;
    const actionBarMarginTop = actionBar ? parseFloat(getComputedStyle(actionBar).marginTop) || 0 : 0;
    const hintStatusH = this.hintStatus && !this.hintStatus.hidden
      ? this.hintStatus.getBoundingClientRect().height
      : 0;
    const shellStyles = shell ? getComputedStyle(shell) : null;
    const shellPaddingV = shellStyles
      ? (parseFloat(shellStyles.paddingTop) || 0) + (parseFloat(shellStyles.paddingBottom) || 0)
      : 0;
    const wrapStyles = getComputedStyle(wrap);
    const wrapPaddingV =
      (parseFloat(wrapStyles.paddingTop) || 0) + (parseFloat(wrapStyles.paddingBottom) || 0);
    const wrapPaddingH =
      (parseFloat(wrapStyles.paddingLeft) || 0) + (parseFloat(wrapStyles.paddingRight) || 0);

    const reservedHeight =
      shellPaddingV +
      topBarH +
      topBarMarginBottom +
      actionBarH +
      actionBarMarginTop +
      hintStatusH +
      wrapPaddingV;
    const availableHeight = window.innerHeight - reservedHeight;
    const availableWidth = wrap.clientWidth - wrapPaddingH;

    const maxSize = Math.max(120, Math.min(availableWidth, availableHeight));
    this.canvasRenderer.resize(this.engine.getState(), maxSize);
    this.canvasRenderer.render(
      this.engine.getState(),
      this.input2d?.getActiveColor() ?? null,
    );
  }

  private handleViewChange(): void {
    this.scene3d?.cycleView(this.getViewportInsets());
  }

  private getViewportInsets(): ViewportInsets {
    const topBar = this.root.querySelector('.game-overlay--top');
    const bottomBar = this.root.querySelector('.game-overlay--bottom');
    const rotate = this.root.querySelector('.rotate-controls');

    let top = topBar?.getBoundingClientRect().height ?? 64;
    if (this.levelNav && !this.levelNav.hasAttribute('hidden')) {
      top += this.levelNav.getBoundingClientRect().height;
    }

    let bottom = bottomBar?.getBoundingClientRect().height ?? 88;
    const rotateRect = rotate?.getBoundingClientRect();
    if (rotateRect) {
      bottom = Math.max(bottom, window.innerHeight - rotateRect.top + 8);
    }

    return { top, bottom };
  }

  private syncUi(): void {
    if (!this.engine) return;

    const state = this.engine.getState();
    // gear's 2D sounds come from the renderer callback; gear 3D and all other styles from here
    if (this.currentVisualStyle !== 'gear' || this.mode === '3d') {
      for (const event of collectGearSoundEvents(state, this.gearSoundTracker)) {
        this.gearSoundPlayer.play(event);
      }
    }

    if (this.mode === '3d' && this.scene3d) {
      this.scene3d.sync(
        state,
        this.input3d?.getActiveColor() ?? null,
      );
    } else if (this.canvasRenderer) {
      this.canvasRenderer.render(
        state,
        this.input2d?.getActiveColor() ?? null,
      );
    }

    this.undoBtn.disabled = !this.engine.canUndo();
    this.updateHintRevealButton();
  }

  private updateHintRevealButton(): void {
    if (!this.hintRevealBtn || !this.hintRevealBadge || !this.engine) return;
    const remaining = this.engine.getState().hintRevealsRemaining;
    if (remaining > 0) {
      this.hintRevealBtn.disabled = false;
      this.hintRevealBadge.textContent = String(remaining);
      this.hintRevealBtn.title = 'Путь';
    } else {
      // Rewarded-ad flow isn't wired up yet — disable rather than show a dead promise.
      this.hintRevealBtn.disabled = true;
      this.hintRevealBadge.textContent = '';
      this.hintRevealBtn.title = 'Путь';
    }
  }

  private handleUndo(): void {
    if (!this.engine) return;
    this.clearHint();
    this.engine.undo();
    this.syncUi();
  }

  private handleClear(): void {
    if (!this.engine) return;
    const state = this.engine.getState();
    for (const color of state.paths.keys()) {
      const path = state.paths.get(color);
      if (path && path.length > 0) {
        this.engine.clearPath(color);
      }
    }
    this.syncUi();
  }

  private handleReset(): void {
    if (!this.engine) return;
    this.clearHint();
    this.engine.reset();
    this.startLevelTimer();
    this.syncUi();
  }

  /** (Re)starts the visible elapsed-time counter for the current attempt — called
   *  on every fresh level load and on reset/replay, so a restarted attempt gets a
   *  clean shot at the 3rd star's time bonus instead of inheriting stale elapsed time. */
  private startLevelTimer(): void {
    this.stopLevelTimer();
    this.levelStartedAt = Date.now();
    if (this.pausedAt !== null) this.pausedAt = this.levelStartedAt;
    if (this.pauseReasons.has('manual')) this.setPause('manual', false);
    this.updateLevelTimerDisplay();
    this.timerIntervalId = window.setInterval(() => this.updateLevelTimerDisplay(), 1000);
  }

  private stopLevelTimer(): void {
    if (this.timerIntervalId !== null) {
      window.clearInterval(this.timerIntervalId);
      this.timerIntervalId = null;
    }
  }

  private updateLevelTimerDisplay(): void {
    if (!this.levelTimerEl) return;
    const elapsed = Math.max(0, Math.floor((this.nowForTimer() - this.levelStartedAt) / 1000));
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    this.levelTimerEl.textContent = `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  private getElapsedTimeSec(): number {
    return Math.round((this.nowForTimer() - this.levelStartedAt) / 1000);
  }

  /** 'nudge' highlights which color to work on (free, unlimited); 'reveal' draws a
   *  bounded stretch of that color's remaining path (limited, never the final cell alone). */
  private handleHint(kind: HintKind): void {
    if (!this.engine || !this.currentLevel || this.mode === '3d') return;
    if (kind === 'reveal' && this.engine.getState().hintRevealsRemaining <= 0) return;

    const hint = computeHint(this.currentLevel, this.engine.getState(), kind);

    if (!hint) {
      this.activeHintKind = null;
      this.canvasRenderer?.setHint(null);
      this.setHintStatus(null);
      return;
    }

    if (!hint.conflict) {
      this.engine.useHint(kind);
    }
    this.activeHintKind = kind;
    this.canvasRenderer?.setHint(hint);
    this.setHintStatus(hint.color, kind, hint.conflict);
    this.syncUi();
  }

  private clearHint(): void {
    if (this.activeHintKind === null) return;
    this.activeHintKind = null;
    this.canvasRenderer?.setHint(null);
    this.setHintStatus(null);
  }

  private setHintStatus(color: string | null, kind?: HintKind, conflict?: boolean): void {
    if (!this.hintStatus) return;
    const wasHidden = this.hintStatus.hidden;

    if (!color || !kind) {
      this.hintStatus.hidden = true;
      this.hintStatus.textContent = '';
    } else {
      const messages: Record<HintKind, string> = {
        nudge: 'Продолжай эту нить',
        reveal: 'Вот отрезок пути',
      };
      const text = conflict ? 'Эта нить проведена не туда — отмени её и попробуй снова' : messages[kind];
      const dotColor = conflict ? '#EF4444' : (COLORS[color] ?? '#888');

      this.hintStatus.hidden = false;
      this.hintStatus.innerHTML = `<span class="hint-status-dot" style="background:${dotColor}"></span>${text}`;
    }

    if (this.hintStatus.hidden !== wasHidden) this.handleResize();
  }

  private handleWin(): void {
    this.stopLevelTimer();

    if (this.specialMode === 'daily') {
      this.showDailyWinModal(markDailyCompleted());
      return;
    }
    if (this.specialMode === 'endless') {
      this.showEndlessWinModal(this.endlessRound, recordEndlessRun(this.endlessRound).bestStreak);
      return;
    }

    this.completedLevels.add(this.currentLevelId);
    const hintsUsed = this.engine
      ? HINT_REVEALS_PER_LEVEL - this.engine.getState().hintRevealsRemaining
      : 0;
    const undoCount = this.engine?.getUndoCount() ?? 0;
    const redrawCount = this.engine?.getRedrawCount() ?? 0;
    const timeSec = this.getElapsedTimeSec();
    const parTimeSec = this.currentLevel
      ? getParTimeSec(this.currentLevel.size, this.currentLevel.pairs.length)
      : 0;
    markLevelCompleted(this.currentLevelId, hintsUsed, undoCount, redrawCount, timeSec, parTimeSec);
    void syncScore().catch(() => {
      // Best-effort — the leaderboard tab re-syncs on open anyway, no need to
      // surface a network hiccup mid-win-modal.
    });
    const missedTimeBonusOnly =
      hintsUsed === 0 && undoCount + redrawCount <= 10 && timeSec > parTimeSec;
    this.showWinModal(
      calculateStars(hintsUsed, undoCount, redrawCount, timeSec, parTimeSec),
      timeSec,
      parTimeSec,
      missedTimeBonusOnly,
    );
  }

  private getNextLevelId(): string | null {
    if (!this.pack) return null;
    const visible = this.getVisibleLevels();
    const index = visible.findIndex((l) => l.id === this.currentLevelId);
    return visible[index + 1]?.id ?? null;
  }

  private getPrevLevelId(): string | null {
    if (!this.pack) return null;
    const visible = this.getVisibleLevels();
    const index = visible.findIndex((l) => l.id === this.currentLevelId);
    if (index <= 0) return null;
    return visible[index - 1]?.id ?? null;
  }

  private updateLevelNavButtons(): void {
    this.prevLevelBtn.disabled = this.getPrevLevelId() === null;
    this.nextLevelBtn.disabled = this.getNextLevelId() === null;
  }

  private showWinModal(
    stars: number,
    timeSec?: number,
    parTimeSec?: number,
    missedTimeBonusOnly = false,
  ): void {
    this.dismissWinModal();

    const nextLevelId = this.getNextLevelId();
    const tierLabel = difficultyTierLabel(this.settings.difficultyTier);
    const timeInfo =
      timeSec !== undefined ? renderWinTimeInfo(timeSec, parTimeSec ?? 0, missedTimeBonusOnly) : '';

    const overlay = document.createElement('div');
    overlay.className = 'win-overlay';
    overlay.innerHTML = nextLevelId
      ? `
      <div class="win-modal" role="dialog" aria-modal="true">
        ${renderWinStars(stars)}
        <h2>Уровень пройден!</h2>
        <p>Все точки соединены, сетка заполнена.</p>
        ${timeInfo}
        <div class="actions">
          <button class="action-btn primary" id="next-level-btn" type="button">Следующий уровень</button>
          <button class="action-btn" id="replay-btn" type="button">Повторить</button>
        </div>
      </div>
    `
      : `
      <div class="win-modal" role="dialog" aria-modal="true">
        <div class="win-icon" aria-hidden="true">★</div>
        <h2>Все уровни пройдены!</h2>
        <p>Вы прошли все уровни режима «${tierLabel}».</p>
        <div class="actions">
          <button class="action-btn primary" id="close-win-btn" type="button">Закрыть</button>
          <button class="action-btn" id="replay-btn" type="button">Повторить</button>
        </div>
      </div>
    `;

    if (nextLevelId) {
      overlay.querySelector('#next-level-btn')?.addEventListener('click', () => {
        this.dismissWinModal();
        this.loadLevel(nextLevelId);
      });
    } else {
      overlay.querySelector('#close-win-btn')?.addEventListener('click', () => {
        this.dismissWinModal();
      });
    }

    overlay.querySelector('#replay-btn')?.addEventListener('click', () => {
      this.dismissWinModal();
      this.handleReset();
    });

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) this.dismissWinModal();
    });

    document.body.appendChild(overlay);
    this.winOverlay = overlay;
  }

  private dismissWinModal(): void {
    this.winOverlay?.remove();
    this.winOverlay = null;
  }

  private showDailyWinModal(progress: DailyProgress): void {
    this.dismissWinModal();

    const overlay = document.createElement('div');
    overlay.className = 'win-overlay';
    overlay.innerHTML = `
      <div class="win-modal" role="dialog" aria-modal="true">
        <div class="win-icon" aria-hidden="true">📅</div>
        <h2>Уровень дня пройден!</h2>
        <p>Серия: ${progress.streak} ${daysWord(progress.streak)} подряд · лучшая серия: ${progress.bestStreak}</p>
        <div class="actions">
          <button class="action-btn primary" id="close-win-btn" type="button">Закрыть</button>
          <button class="action-btn" id="replay-btn" type="button">Повторить</button>
        </div>
      </div>
    `;

    overlay.querySelector('#close-win-btn')?.addEventListener('click', () => {
      this.dismissWinModal();
      this.exitSpecialMode();
    });
    overlay.querySelector('#replay-btn')?.addEventListener('click', () => {
      this.dismissWinModal();
      this.handleReset();
    });
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) this.dismissWinModal();
    });

    document.body.appendChild(overlay);
    this.winOverlay = overlay;
  }

  private showEndlessWinModal(round: number, bestStreak: number): void {
    this.dismissWinModal();

    const overlay = document.createElement('div');
    overlay.className = 'win-overlay';
    overlay.innerHTML = `
      <div class="win-modal" role="dialog" aria-modal="true">
        <div class="win-icon" aria-hidden="true">♾️</div>
        <h2>Раунд ${round} пройден!</h2>
        <p>Лучшая серия: ${bestStreak}</p>
        <div class="actions">
          <button class="action-btn primary" id="next-endless-btn" type="button">Следующий раунд</button>
          <button class="action-btn" id="exit-endless-btn" type="button">Выйти</button>
        </div>
      </div>
    `;

    overlay.querySelector('#next-endless-btn')?.addEventListener('click', () => {
      this.dismissWinModal();
      this.startEndlessLevel(round + 1);
    });
    overlay.querySelector('#exit-endless-btn')?.addEventListener('click', () => {
      this.dismissWinModal();
      this.exitSpecialMode();
    });
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) this.dismissWinModal();
    });

    document.body.appendChild(overlay);
    this.winOverlay = overlay;
  }

  private showLevelPicker(): void {
    if (!this.pack) return;
    this.dismissLevelPicker();
    this.setPause('picker', true);

    const dailyProgress = loadDailyProgress();

    this.levelPickerOverlay = openLevelPicker({
      levels: this.pack.levels,
      currentLevelId: this.currentLevelId,
      completedLevelIds: this.completedLevels,
      difficultyTier: this.settings.difficultyTier,
      dailyInfo: { streak: dailyProgress.streak, completedToday: isDailyCompletedToday() },
      endlessInfo: { bestStreak: loadEndlessProgress().bestStreak },
      onSelectLevel: (levelId) => {
        this.loadLevel(levelId);
      },
      onSelectSpecial: (mode) => {
        if (mode === 'daily') this.startDailyLevel();
        else this.startEndlessLevel(1);
      },
      onDifficultyChange: (tier) => {
        const next = { ...this.settings, difficultyTier: tier };
        this.settings = next;
        saveSettings(next);

        if (!this.pack) return;
        const clamped = clampLevelToTier(this.currentLevelId, tier, this.pack.levels);
        if (clamped !== this.currentLevelId) {
          this.loadLevel(clamped);
        } else {
          this.updateLevelLabel();
        }
      },
      onClose: () => {
        this.levelPickerOverlay = null;
        this.setPause('picker', false);
      },
    });
  }

  private dismissLevelPicker(): void {
    this.levelPickerOverlay?.remove();
    this.levelPickerOverlay = null;
    this.setPause('picker', false);
  }

  private showSettingsModal(): void {
    this.dismissSettingsModal();
    this.setPause('settings', true);

    const webglAvailable = canUse3D();
    const { use3d, sound, music, animations } = this.settings;

    const overlay = document.createElement('div');
    overlay.className = 'settings-overlay';
    overlay.innerHTML = `
      <div class="settings-modal" role="dialog" aria-modal="true" aria-labelledby="settings-title">
        <h2 id="settings-title">Настройки</h2>

        <div class="settings-section">
          <div class="settings-section-title">Игра</div>
          <div class="settings-row">
            <div class="settings-row-label">
              <span class="settings-row-title">3D режим</span>
              <span class="settings-row-desc">Объёмное поле с вращением камеры</span>
            </div>
            <label class="toggle-switch">
              <input
                type="checkbox"
                id="setting-3d"
                role="switch"
                ${use3d ? 'checked' : ''}
                ${webglAvailable ? '' : 'disabled'}
              />
              <span class="toggle-track" aria-hidden="true"></span>
            </label>
          </div>
          <div class="settings-row">
            <div class="settings-row-label">
              <span class="settings-row-title">Звук</span>
              <span class="settings-row-desc">Звуковые эффекты при действиях</span>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" id="setting-sound" role="switch" ${sound ? 'checked' : ''} />
              <span class="toggle-track" aria-hidden="true"></span>
            </label>
          </div>
          <div class="settings-row">
            <div class="settings-row-label">
              <span class="settings-row-title">Музыка</span>
              <span class="settings-row-desc">Фоновая мелодия во время игры</span>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" id="setting-music" role="switch" ${music ? 'checked' : ''} />
              <span class="toggle-track" aria-hidden="true"></span>
            </label>
          </div>
          <div class="settings-row settings-row--last">
            <div class="settings-row-label">
              <span class="settings-row-title">Анимации</span>
              <span class="settings-row-desc">Плавные переходы и эффекты победы</span>
            </div>
            <label class="toggle-switch">
              <input
                type="checkbox"
                id="setting-animations"
                role="switch"
                ${animations ? 'checked' : ''}
              />
              <span class="toggle-track" aria-hidden="true"></span>
            </label>
          </div>
        </div>

        ${
          webglAvailable
            ? ''
            : '<p class="settings-hint">WebGL недоступен — 3D режим отключён</p>'
        }
        <button class="action-btn primary" id="open-levels-btn" type="button">Выбор уровня</button>
        <button class="action-btn" id="settings-close" type="button">Закрыть</button>
      </div>
    `;

    const toggle3d = overlay.querySelector('#setting-3d') as HTMLInputElement;
    const toggleSound = overlay.querySelector('#setting-sound') as HTMLInputElement;
    const toggleMusic = overlay.querySelector('#setting-music') as HTMLInputElement;
    const toggleAnimations = overlay.querySelector('#setting-animations') as HTMLInputElement;

    const applySettings = (next: GameSettings, reloadMode = false): void => {
      this.settings = next;
      saveSettings(next);
      applyAnimationSetting(next.animations);

      if (reloadMode) {
        this.dismissSettingsModal();
        this.applyDifficultyAndMode(next);
        return;
      }

      if (!this.pack) return;

      const clamped = clampLevelToTier(
        this.currentLevelId,
        next.difficultyTier,
        this.pack.levels,
      );
      if (clamped !== this.currentLevelId) {
        this.loadLevel(clamped);
      } else {
        this.updateLevelLabel();
      }
    };

    toggle3d.addEventListener('change', () => {
      applySettings({ ...this.settings, use3d: toggle3d.checked }, true);
    });

    toggleSound.addEventListener('change', () => {
      this.gearSoundPlayer.setEnabled(toggleSound.checked);
      applySettings({ ...this.settings, sound: toggleSound.checked });
    });

    toggleMusic.addEventListener('change', () => {
      this.musicPlayer.setEnabled(toggleMusic.checked);
      applySettings({ ...this.settings, music: toggleMusic.checked });
    });

    toggleAnimations.addEventListener('change', () => {
      applySettings({ ...this.settings, animations: toggleAnimations.checked });
    });

    overlay.querySelector('#open-levels-btn')?.addEventListener('click', () => {
      this.dismissSettingsModal();
      this.showLevelPicker();
    });

    overlay.querySelector('#settings-close')?.addEventListener('click', () => {
      this.dismissSettingsModal();
    });

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) this.dismissSettingsModal();
    });

    document.body.appendChild(overlay);
    this.settingsOverlay = overlay;
  }

  private updateLevelLabel(): void {
    if (!this.engine || !this.pack) return;
    const level = getLevelById(this.pack.levels, this.currentLevelId);
    if (!level) return;
    const visualStyle = getVisualStyleForLevel(this.currentLevelId);
    const styleLabel = getVisualStyleLabel(visualStyle);
    const difficultyLabel = levelDifficultyLabel(level.difficulty);
    this.setLevelLabelText(
      `Уровень ${this.currentLevelId.replace('e-', '')} · ${level.size}×${level.size} · ${styleLabel} · ${difficultyLabel}`,
    );
    this.updateLevelNavButtons();
  }

  private setLevelLabelText(text: string): void {
    this.levelLabel.textContent = text;
    this.levelLabel.title = text;
  }

  private applyDifficultyAndMode(next: GameSettings): void {
    const newMode = resolveGameMode(next);
    const levelId = this.pack
      ? clampLevelToTier(this.currentLevelId, next.difficultyTier, this.pack.levels)
      : this.currentLevelId;
    const pack = this.pack;

    if (newMode === this.mode) {
      if (pack && levelId !== this.currentLevelId) {
        this.loadLevel(levelId);
      } else {
        this.updateLevelLabel();
      }
      return;
    }

    this.dismissWinModal();
    this.destroyInput();
    this.destroyRenderer();
    this.engine = null;
    this.mode = newMode;
    this.renderShell();
    this.setupTheme();

    if (pack) {
      this.pack = pack;
      this.loadLevel(levelId);
    } else {
      void this.init();
    }
  }

  private dismissSettingsModal(): void {
    this.settingsOverlay?.remove();
    this.settingsOverlay = null;
    this.setPause('settings', false);
  }
}
